import { formatCount, formatLocalDate, type LocalDate } from '@saay/formatting';
import { KpiCard, ListRow, Money } from '@saay/blocks';
import type { ReactNode } from 'react';
import { CoinsIcon, StoreIcon, WalletIcon } from 'lucide-react';

import { QueryState, RequireArea, usePageTitle } from '../components/guard.js';
import { AdminListTable, type DataTableColumn } from '../components/admin-list-table.js';
import { EmptyState, PageHeader, Section, Skeleton } from '../kit.js';
import type {
  CommissionPayoutAttempt,
  MarketingAssignmentSummary,
  MarketingCommissionLine,
} from '../marketing-types.js';
import {
  listStatus,
  pageRows,
  useCommissionPayoutAttempts,
  useMarketerCommissionSummary,
  useMarketingAssignments,
  useMarketingCommissions,
  useMarketingRecipients,
  useMarketingStructures,
} from '../queries.js';
import { CodeText, KpiFigure, MarketingStatus, phoneSummary } from './marketing-ui.js';

function showDate(value: string): string {
  return formatLocalDate(value as LocalDate);
}

function scopeName(row: MarketingAssignmentSummary): string {
  if (row.scope.type === 'MERCHANT') return row.scope.merchant?.nameFa ?? 'فروشگاه';
  return row.scope.organization?.nameFa ?? 'سازمان';
}

const assignmentColumns: readonly DataTableColumn<MarketingAssignmentSummary>[] = [
  { header: 'محل', id: 'scope', render: (row) => scopeName(row) },
  { header: 'ساختار', id: 'structure', render: (row) => row.structure.nameFa },
  { header: 'وضعیت', id: 'status', render: (row) => <MarketingStatus value={row.status} /> },
];

const lineColumns: readonly DataTableColumn<MarketingCommissionLine>[] = [
  {
    header: 'فروش',
    id: 'sale',
    render: (row) => <CodeText>{row.sale.saleReference}</CodeText>,
  },
  { header: 'فروشگاه', id: 'merchant', render: (row) => row.merchant.nameFa },
  {
    header: 'مبلغ قسط',
    id: 'amount',
    numeric: true,
    render: (row) => <Money rials={row.payableAmountIrr} />,
  },
  { header: 'وضعیت', id: 'status', render: (row) => <MarketingStatus value={row.status} /> },
];

const payoutColumns: readonly DataTableColumn<CommissionPayoutAttempt>[] = [
  {
    header: 'مبلغ',
    id: 'amount',
    numeric: true,
    render: (row) => <Money rials={row.amountIrr} />,
  },
  { header: 'وضعیت', id: 'status', render: (row) => <MarketingStatus value={row.status} /> },
  { header: 'تاریخ', id: 'date', render: (row) => showDate(row.batch.scheduledPayoutDate) },
];

export function MyCommissionsPage() {
  usePageTitle('کمیسیون‌های من');
  return (
    <RequireArea area="myCommissions">
      {(self) => <MyCommissionsBody recipientId={self.marketingRecipientId} />}
    </RequireArea>
  );
}

function MyCommissionsBody({ recipientId }: { readonly recipientId: string | null }) {
  const enabled = recipientId !== null;
  const filters = { marketingRecipientId: recipientId ?? '' };
  const summary = useMarketerCommissionSummary(enabled);
  const recipients = useMarketingRecipients(filters, enabled);
  const structures = useMarketingStructures(filters, enabled);
  const assignments = useMarketingAssignments(filters, enabled);
  const lines = useMarketingCommissions(filters, enabled);
  const payouts = useCommissionPayoutAttempts(filters, enabled);
  if (!enabled) {
    return (
      <>
        <PageHeader title="کمیسیون‌های من" />
        <EmptyState title="حساب بازاریابی شما هنوز وصل نشده است." />
      </>
    );
  }
  const data = summary.data;
  return (
    <>
      <PageHeader
        description="کمیسیون فروش‌هایی که به شما وصل است. مبلغ‌ها از سرور می‌آید."
        title="کمیسیون‌های من"
      />
      <QueryState
        error={summary.error}
        onRetry={() => void summary.refetch()}
        skeleton={<Skeleton label="در حال بارگذاری کمیسیون‌های من…" lines={6} />}
        status={summary.status}
      >
        {data === undefined ? null : (
          <div className="flex flex-col gap-4">
            <p>{data.recipient.displayNameFa ?? data.recipient.recipientCode}</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              <KpiCard
                icon={WalletIcon}
                label="ماندهٔ پرداخت‌نشده"
                tone="gold"
                value={<Money rials={data.unpaidIrr} size="lg" />}
              />
              <KpiCard
                icon={CoinsIcon}
                label="پرداخت بعدی"
                tone="olive"
                value={
                  data.nextPayout === null ? (
                    <KpiFigure>ندارد</KpiFigure>
                  ) : (
                    <Money rials={data.nextPayout.payableIrr} size="lg" />
                  )
                }
              />
              <KpiCard
                icon={StoreIcon}
                label="اتصال فعال"
                tone="forest"
                value={<KpiFigure>{formatCount(data.activeAssignmentCount)}</KpiFigure>}
              />
            </div>
            <OwnList
              columns={assignmentColumns}
              empty="اتصال فعالی ندارید"
              error={assignments.error}
              mobileSummary={(row) =>
                phoneSummary({
                  meta: row.structure.nameFa,
                  status: row.status,
                  title: scopeName(row),
                })
              }
              onRetry={() => void assignments.refetch()}
              rows={pageRows(assignments.data)}
              status={listStatus(assignments)}
              title="اتصال‌ها"
            />
            <Section id="my-structures" title="ساختارها">
              <ul className="flex flex-col gap-2">
                {pageRows(structures.data).map((structure) => (
                  <li key={structure.id}>
                    <ListRow
                      description={
                        structure.publishedVersion === null
                          ? structure.code
                          : `${structure.code} · نسخه ${formatCount(structure.publishedVersion.versionNumber)}`
                      }
                      look="tile"
                      title={structure.nameFa}
                    />
                  </li>
                ))}
              </ul>
              {pageRows(recipients.data).length === 0 ? null : (
                <p className="sr-only">{pageRows(recipients.data)[0]?.recipientCode}</p>
              )}
            </Section>
            <OwnList
              columns={lineColumns}
              empty="کمیسیونی ثبت نشده است"
              error={lines.error}
              mobileSummary={(row) =>
                phoneSummary({
                  amountIrr: row.payableAmountIrr,
                  meta: `${row.merchant.nameFa} · ${showDate(row.scheduledPayoutDate)}`,
                  status: row.status,
                  title: <CodeText>{row.sale.saleReference}</CodeText>,
                })
              }
              onRetry={() => void lines.refetch()}
              rows={pageRows(lines.data)}
              status={listStatus(lines)}
              title="کمیسیون‌ها"
            />
            <OwnList
              columns={payoutColumns}
              empty="پرداختی ثبت نشده است"
              error={payouts.error}
              mobileSummary={(row) =>
                phoneSummary({
                  amountIrr: row.amountIrr,
                  meta: showDate(row.batch.scheduledPayoutDate),
                  status: row.status,
                  title: 'پرداخت',
                })
              }
              onRetry={() => void payouts.refetch()}
              rows={pageRows(payouts.data)}
              status={listStatus(payouts)}
              title="پرداخت‌ها"
            />
          </div>
        )}
      </QueryState>
    </>
  );
}

function OwnList<Row extends { readonly id: string }>({
  columns,
  empty,
  error,
  mobileSummary,
  onRetry,
  rows,
  status,
  title,
}: {
  readonly columns: readonly DataTableColumn<Row>[];
  readonly empty: string;
  readonly error: unknown;
  readonly mobileSummary: (row: Row) => {
    readonly primary: ReactNode;
    readonly secondary: ReactNode;
  };
  readonly onRetry: () => void;
  readonly rows: readonly Row[];
  readonly status: 'error' | 'pending' | 'success';
  readonly title: string;
}) {
  return (
    <Section id={title} title={title}>
      <QueryState
        empty={<EmptyState title={empty} />}
        error={error}
        isEmpty={rows.length === 0}
        onRetry={onRetry}
        skeleton={<Skeleton label="در حال بارگذاری…" lines={3} />}
        status={status}
      >
        <AdminListTable
          caption={title}
          columns={columns}
          mobileSummary={mobileSummary}
          rowKey={(row) => row.id}
          rows={rows}
        />
      </QueryState>
    </Section>
  );
}
